import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { X, Clock, AlertTriangle, CheckCircle } from "lucide-react";

export default function RunningLateModal({ visit, onClose, onSuccess }) {
  const [delayOptions, setDelayOptions] = useState(null);
  const [selectedLength, setSelectedLength] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDelayOptions();
  }, []);

  const loadDelayOptions = async () => {
    try {
      const { data } = await api.get("/evv/delay-options");
      setDelayOptions(data);
    } catch (err) {
      console.error("Failed to load delay options:", err);
      setError("Failed to load options. Please try again.");
    }
  };

  const handleSubmit = async () => {
    if (!selectedLength || !selectedReason) {
      setError("Please select delay time and reason");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post("/evv/running-late", {
        visit_id: visit.id,
        delay_length: selectedLength,
        delay_reason: selectedReason,
        note: note.trim() || null,
      });
      
      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to report delay");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
        <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 text-center animate-in slide-in-from-bottom duration-300">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Office Notified</h3>
          <p className="text-gray-500 mt-2">
            The office has been notified that you&apos;re running late.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300">
        {/* Header */}
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Running Late</h2>
              <p className="text-sm text-gray-500">Let the office know</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="close-running-late-modal"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-red-50 text-red-700">
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Client Info */}
          {visit && (
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm text-gray-500">Visit with</div>
              <div className="font-medium text-gray-900">{visit.client_name}</div>
            </div>
          )}

          {/* Delay Length Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              How long will you be delayed?
            </label>
            <div className="grid grid-cols-2 gap-2">
              {delayOptions?.delay_lengths?.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSelectedLength(opt.key)}
                  className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                    selectedLength === opt.key
                      ? "border-[#06372B] bg-[#06372B]/5 text-[#06372B]"
                      : "border-gray-200 text-gray-700 hover:border-gray-300"
                  }`}
                  data-testid={`delay-length-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Delay Reason Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              What&apos;s the reason?
            </label>
            <div className="grid grid-cols-2 gap-2">
              {delayOptions?.delay_reasons?.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSelectedReason(opt.key)}
                  className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                    selectedReason === opt.key
                      ? "border-[#06372B] bg-[#06372B]/5 text-[#06372B]"
                      : "border-gray-200 text-gray-700 hover:border-gray-300"
                  }`}
                  data-testid={`delay-reason-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Optional Note */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Additional note (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Any details to share with the office..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#06372B] focus:ring-1 focus:ring-[#06372B] outline-none transition-colors resize-none text-sm"
              data-testid="delay-note-input"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-100">
          <button
            onClick={handleSubmit}
            disabled={loading || !selectedLength || !selectedReason}
            className="w-full py-3.5 px-4 rounded-xl bg-[#06372B] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#0a4a3a] transition-colors"
            data-testid="submit-running-late"
          >
            {loading ? "Sending..." : "Notify Office"}
          </button>
        </div>
      </div>
    </div>
  );
}
